import { secp256k1 } from '@noble/curves/secp256k1';
import { concatBytes } from '@noble/hashes/utils';
import { hex } from '@scure/base';
import { HDKey } from '@scure/bip32';
import * as bip39 from '@scure/bip39';
import * as btc from '@scure/btc-signer';
import { keccak256, parseTransaction } from 'viem';
import { privateKeyToAccount } from 'viem/accounts';

import { insideSingleLevelSameOriginIframe } from ':fat/helpers/iframe';
import { reconstructTransactions } from ':fat/workers/blockchain/transactions/reconstruct';
import { EthTransaction, Transaction } from ':fat/workers/blockchain/types';

import { getNetworkConfig, Network } from './networks';

const SIGHASH_BITCOINCASHBIP143 = 0x40;

export class Signer {
  #seed: Uint8Array;

  constructor(mnemonic: string) {
    if (!insideSingleLevelSameOriginIframe()) {
      throw new Error('Cannot instantiate a signer outside the security frame');
    }

    const seed = bip39.mnemonicToSeedSync(mnemonic);
    this.#seed = seed;
  }

  async signTransactions(txs: Record<Network, Transaction[]>) {
    reconstructTransactions(txs);
    const allSignedTxs: Record<Network, Transaction[]> = {} as Record<Network, Transaction[]>;

    for (const [n, networkTxs] of Object.entries(txs)) {
      const network = n as Network;
      const signedTxs: Transaction[] = [];
      for (const tx of networkTxs) {
        if (network === 'eth') {
          const signed = await this.signEthTransaction(tx as EthTransaction);
          signedTxs.push(signed);
        } else {
          this.signUtxoTransaction(network, tx as btc.Transaction);
          signedTxs.push(tx);
        }
      }
      allSignedTxs[network] = signedTxs;
    }

    return allSignedTxs;
  }

  private signUtxoTransaction(network: Exclude<Network, 'eth'>, tx: btc.Transaction) {
    const networkConfig = getNetworkConfig(network);
    const rootKey = HDKey.fromMasterSeed(this.#seed, networkConfig.bip32);

    if (network === 'bch') {
      this.signBchTransaction(tx, rootKey);
    } else {
      // @ts-expect-error because we know we have the full keypair
      tx.sign(rootKey);
    }

    tx.finalize();
  }

  // BCH requires something similar to Ethereum's chain id as part of the
  // signature, to protect against replay attacks. Because `@scure/btc-signer`
  // does not support this with the higher-level `tx.sign` or `tx.signIdx`
  // methods, we have to replicate what those do with some modifications. This
  // implementation is heavily inspired by:
  // https://github.com/CoinSpace/cs-bitcoin-wallet/blob/a509a0b30d58264bbdaf73c69991ba4adca6f7f6/lib/utils.js#L54
  // More details of this difference can be found here:
  // https://github.com/bitcoincashorg/bitcoincash.org/blob/master/spec/uahf-technical-spec.md#req-6-2-mandatory-signature-shift-via-hash-type
  private signBchTransaction(tx: btc.Transaction, rootKey: HDKey) {
    for (let i = 0; i < tx.inputsLength; i++) {
      const { input, value, key } = this.extractInputDetails('bch', tx, i, rootKey);
      const inputType = btc.getInputType(input, true);
      const sighash = inputType.sighash | SIGHASH_BITCOINCASHBIP143;
      const hash = tx.preimageWitnessV0(i, inputType.lastScript, sighash, value);
      const sig = secp256k1.sign(hash, key.privateKey!).toDERRawBytes();
      const partialSig: [Uint8Array, Uint8Array][] = [
        [key.publicKey!, concatBytes(sig, new Uint8Array([sighash]))],
      ];

      tx.updateInput(i, { partialSig }, true);
    }
  }

  private extractInputDetails(network: Network, tx: btc.Transaction, i: number, rootKey: HDKey) {
    const input = tx.getInput(i);
    if (!input) {
      throw new Error(`${network} signing: missing input`);
    }

    const value = input.witnessUtxo?.amount ?? 0n;
    if (value === 0n) {
      throw new Error(`${network} signing: missing value`);
    }

    const derivation = (input.bip32Derivation ?? [])[0];
    if (!derivation) {
      throw new Error(`${network} signing: missing derivation`);
    }

    const [publicKey, { path, fingerprint }] = derivation;
    if (fingerprint !== rootKey.fingerprint) {
      throw new Error(`${network} signing: mismatched fingerprint`);
    }

    let key = rootKey;
    for (const part of path) {
      key = key.deriveChild(part);
    }

    if (hex.encode(publicKey) !== hex.encode(key.publicKey ?? new Uint8Array())) {
      throw new Error(`${network} signing: mismatched public key`);
    }

    const { privateKey } = key;
    if (!privateKey) {
      throw new Error(`${network} signing: no private key`);
    }

    return { input, value, key };
  }

  private async signEthTransaction(tx: EthTransaction): Promise<EthTransaction> {
    const rootKey = HDKey.fromMasterSeed(this.#seed);
    if (tx.fingerprint !== rootKey.fingerprint) {
      throw new Error('Mismatched fingerprint');
    }

    const key = rootKey.derive(tx.fullPath);
    if (hex.encode(key.publicKey!) !== hex.encode(tx.publicKey)) {
      throw new Error('Mismatched public key');
    }

    const account = privateKeyToAccount(`0x${hex.encode(key.privateKey!)}`);
    // @ts-expect-error because we know everything is present
    const signedTxHex = await account.signTransaction(tx.tx);
    const signedTx = parseTransaction(signedTxHex);

    return {
      // @ts-expect-error because we know everything is present
      tx: signedTx,
      hex: signedTxHex,
      hash: keccak256(signedTxHex),
      fingerprint: tx.fingerprint,
      publicKey: tx.publicKey,
      fullPath: tx.fullPath,
    };
  }
}
